/**
 * Lightweight analytics tracker — zero performance impact.
 * - Uses sendBeacon for fire-and-forget delivery
 * - Batches events and flushes every 10 seconds
 * - Auto-tracks page navigations via React Router
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const FLUSH_INTERVAL = 10_000; // 10 seconds
const MAX_BATCH = 30;

// Shared queue (persists across re-renders)
let _queue = [];
let _flushTimer = null;

function _flush() {
  if (_queue.length === 0) return;
  const batch = _queue.splice(0, MAX_BATCH);
  const isPublic = batch[0]?.source === 'website';
  const endpoint = isPublic ? '/api/analytics/track-public' : '/api/analytics/track';
  const body = JSON.stringify({ events: batch });

  // Use sendBeacon for zero-blocking delivery
  // Cookies are sent automatically for same-origin requests
  if (navigator.sendBeacon) {
    if (!isPublic) {
      // Authenticated endpoint — use fetch with credentials to send httpOnly cookie
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'include',
      }).catch(() => {});
    } else {
      const headers = { type: 'application/json' };
      navigator.sendBeacon(endpoint, new Blob([body], headers));
    }
  }
}

function _startFlushTimer() {
  if (_flushTimer) return;
  _flushTimer = setInterval(_flush, FLUSH_INTERVAL);
  // Flush on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', _flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') _flush();
    });
  }
}

/**
 * Track an ATS event (authenticated).
 * @param {string} action - e.g. 'page_view', 'screening_decision', 'resume_download'
 * @param {object} meta - additional context
 */
export function trackEvent(action, meta = {}) {
  _queue.push({
    source: 'ats',
    page: window.location.pathname,
    action,
    meta,
    ts: new Date().toISOString(),
  });
  _startFlushTimer();
}

/**
 * Track a public website event (no auth).
 */
export function trackPublicEvent(action, meta = {}) {
  // Generate/retrieve anonymous visitor ID
  let vid = localStorage.getItem('_hj_vid');
  if (!vid) {
    vid = Math.random().toString(36).substring(2, 10);
    localStorage.setItem('_hj_vid', vid);
  }
  _queue.push({
    source: 'website',
    visitor_id: vid,
    page: window.location.pathname,
    action,
    referrer: document.referrer || '',
    meta,
    ts: new Date().toISOString(),
  });
  _startFlushTimer();
}

/**
 * React hook — auto-tracks page views on route change.
 * Place in Layout.jsx for ATS pages.
 */
export function usePageTracker() {
  const location = useLocation();
  const prevPath = useRef('');

  // No need to read token from localStorage — httpOnly cookie handles auth
  useEffect(() => {
    // Start flush timer on mount
  }, []);

  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      if (location.pathname.startsWith('/app')) {
        trackEvent('page_view');
      }
    }
  }, [location.pathname]);
}

/**
 * React hook — auto-tracks public website page views.
 * Place in CareersPage or public layout.
 */
export function usePublicPageTracker() {
  const location = useLocation();
  const prevPath = useRef('');

  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      if (!location.pathname.startsWith('/app')) {
        trackPublicEvent('page_view');
      }
    }
  }, [location.pathname]);
}
